/**
 * Hand-coded health and medical situational phrases
 * (ESL → Japanese L1 kana).
 * License: CC BY-SA 4.0.
 *
 * Highest authoritative-source-coverage gap (7/8 ESL textbook taxonomies).
 * High-stakes for J-L1 learners traveling or living abroad — clinic/pharmacy
 * interactions amplify language anxiety.
 */

import type { SituationCard } from '../../situations/types.js';

const healthMedical: SituationCard[] = [
  {
    id: 'health.appointment',
    situationId: 'health.appointment',
    front: "I'd like to make an appointment with a doctor, please.",
    back: '医師の予約を取りたいのですが。',
    category: 'health-medical',
    cefr: 'A1',
    translations: { ja: 'いしのよやくをとりたいのですが。' },
    speechAct: 'request',
    formality: 'polite',
    channel: 'phone',
    tags: ['appointment']
  },
  {
    id: 'health.symptom-headache',
    situationId: 'health.symptom-headache',
    front: "I've had a headache for two days.",
    back: '二日前から頭痛がします。',
    category: 'health-medical',
    cefr: 'A2',
    translations: { ja: 'ふつかまえからずつうがします。' },
    speechAct: 'opinion',
    formality: 'polite',
    channel: 'in-person'
  },
  {
    id: 'health.where-pain',
    situationId: 'health.where-pain',
    front: 'It hurts right here, especially when I move.',
    back: 'ちょうどここが痛くて、動かすと特に痛みます。',
    category: 'health-medical',
    cefr: 'A2',
    translations: { ja: 'ちょうどここがいたくて、うごかすととくにいたみます。' },
    speechAct: 'opinion',
    formality: 'polite',
    channel: 'in-person'
  },
  {
    id: 'health.allergic-medication',
    situationId: 'health.allergic-medication',
    front: "I'm allergic to penicillin — is that going to be a problem?",
    back: 'ペニシリンアレルギーがあるのですが、大丈夫でしょうか？',
    category: 'health-medical',
    cefr: 'B1',
    translations: { ja: 'ペニシリンアレルギーがあるのですが、だいじょうぶでしょうか？' },
    speechAct: 'request',
    formality: 'polite',
    channel: 'in-person',
    tags: ['safety']
  },
  {
    id: 'health.refill-prescription',
    situationId: 'health.refill-prescription',
    front: "Could I get my prescription refilled, please?",
    back: '処方箋を再調剤していただけますか？',
    category: 'health-medical',
    cefr: 'B1',
    translations: { ja: 'しょほうせんをさいちょうざいしていただけますか？' },
    speechAct: 'request',
    formality: 'polite',
    channel: 'in-person',
    tags: ['pharmacy']
  },
  {
    id: 'health.insurance-question',
    situationId: 'health.insurance-question',
    front: 'Does my insurance cover this procedure?',
    back: 'これは私の保険の対象になりますか？',
    category: 'health-medical',
    cefr: 'B1',
    translations: { ja: 'これはわたしのほけんのたいしょうになりますか？' },
    speechAct: 'request',
    formality: 'polite',
    channel: 'in-person',
    tags: ['insurance']
  },
  {
    id: 'health.feeling-better',
    situationId: 'health.feeling-better',
    front: "I'm feeling much better, thanks for asking.",
    back: 'おかげさまでだいぶよくなりました。',
    category: 'health-medical',
    cefr: 'A2',
    translations: { ja: 'おかげさまでだいぶよくなりました。' },
    speechAct: 'gratitude',
    formality: 'polite',
    channel: 'in-person'
  },
  {
    id: 'health.cant-afford',
    situationId: 'health.cant-afford',
    front: "I'm worried about the cost — are there any cheaper alternatives?",
    back: '費用が心配なのですが、もう少し安い選択肢はありますか？',
    category: 'health-medical',
    cefr: 'B1',
    translations: { ja: 'ひようがしんぱいなのですが、もうすこしやすいせんたくしはありますか？' },
    speechAct: 'request',
    formality: 'polite',
    channel: 'in-person',
    pragmaticNote: {
      en: 'Discussing cost openly with US healthcare staff is normal and expected — they will often help find cheaper options. Don\'t hide financial concerns.',
      ja: '米国の医療現場では費用の相談は普通。隠さずに伝えると別の選択肢を提案してもらえます。'
    }
  },
  {
    id: 'health.followup-test',
    situationId: 'health.followup-test',
    front: "When should I expect to hear back about the test results?",
    back: '検査結果はいつ頃わかりますか？',
    category: 'health-medical',
    cefr: 'B1',
    translations: { ja: 'けんさけっかはいつごろわかりますか？' },
    speechAct: 'request',
    formality: 'polite',
    channel: 'in-person'
  },
  {
    id: 'health.specialist-referral',
    situationId: 'health.specialist-referral',
    front: "I'd really appreciate a referral to a specialist if you think that's warranted.",
    back: '必要であれば、専門医を紹介していただけるとありがたいのですが。',
    category: 'health-medical',
    cefr: 'B2',
    translations: { ja: 'ひつようであれば、せんもんいをしょうかいしていただけるとありがたいのですが。' },
    speechAct: 'request',
    formality: 'formal',
    channel: 'in-person'
  },
  {
    id: 'health.discomfort-medication',
    situationId: 'health.discomfort-medication',
    front: "Since starting the new medication, I've been feeling a bit off — is that normal?",
    back: '新しい薬を飲み始めてから少し体調がよくないのですが、これは普通のことですか？',
    category: 'health-medical',
    cefr: 'B2',
    translations: { ja: 'あたらしいくすりをのみはじめてからすこしたいちょうがよくないのですが、これはふつうのことですか？' },
    speechAct: 'request',
    formality: 'polite',
    channel: 'in-person'
  }
];

export default healthMedical;
